import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api';
import type { UserProfile, LoginRequest, RegisterRequest } from '@/shared';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserProfile | null>(null);
  const initialized = ref(false);
  const loading = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const kycLevel = computed(() => user.value?.kyc_level ?? 0);

  async function initialize() {
    if (initialized.value) return;

    // With HTTPOnly cookies, we just try to fetch the profile
    // The cookies will be sent automatically with the request
    try {
      await fetchProfile();
    } catch {
      // Not authenticated or session expired - that's fine
      user.value = null;
    }

    initialized.value = true;
  }

  async function login(credentials: LoginRequest) {
    loading.value = true;
    try {
      const response = await api.auth.login(credentials);

      if (response.requires_2fa) {
        return {
          requires_2fa: true,
          two_factor_token: response.two_factor_token,
          available_methods: response.available_methods || ['totp'],
        };
      }

      // User data is in the response
      user.value = response.user!;

      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  async function verify2FA(twoFactorToken: string, code: string, method: 'totp' | 'email' | 'sms' = 'totp') {
    loading.value = true;
    try {
      const response = await api.auth.verify2FA(twoFactorToken, code, method);
      user.value = response.user;
      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  async function register(data: RegisterRequest) {
    loading.value = true;
    try {
      await api.auth.register(data);
      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  async function fetchProfile() {
    const profile = await api.user.getProfile();
    user.value = profile;
  }

  async function logout() {
    try {
      await api.auth.logout();
    } finally {
      user.value = null;
    }
  }

  async function logoutAll() {
    try {
      await api.auth.logoutAll();
    } finally {
      user.value = null;
    }
  }

  return {
    user,
    initialized,
    loading,
    isAuthenticated,
    kycLevel,
    initialize,
    login,
    verify2FA,
    register,
    fetchProfile,
    logout,
    logoutAll,
  };
});
